import { APP_ID, isDevMode, NgModule, signal } from '@angular/core';
import { AgrBrowserModule, I18nBrowserEffects } from '@next/core-lib/browser';
import { provideAgrCore } from '@next/core-lib/app';
import { CoreEffects } from '@next/core-lib';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects } from '@next/core-lib/i18n';
import { NavigationEffects } from '@next/core-lib/navigation';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { SiteEffects } from '@next/core-lib/site';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { AppEffects } from './store/effects';
import { appReducer } from './store/reducer';
import { environment } from 'src/environments/environment';
import { parseAgrEnvironment } from '@next/core-lib/environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

const parseEnvironment = (config: Record<string, string | boolean | undefined>) => ({
  ...parseAgrEnvironment(config as Record<string, string>),
  FIELD_ADVICE_TEMPLATE_REBUILD_DONE:
    config.FIELD_ADVICE_TEMPLATE_REBUILD_DONE === true ||
    config.FIELD_ADVICE_TEMPLATE_REBUILD_DONE === 'true',
});

export const setupRuntimeConfig = (async () => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

@NgModule({
  imports: [AppModule, AgrBrowserModule],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => {
        return loadedConfig();
      },
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideStore(),
    provideAgrCore(),
    provideAgrRouterStore(),
    provideState('app', appReducer),
    provideEffects([
      AppEffects,
      CoreEffects,
      I18nEffects,
      I18nBrowserEffects,
      SiteEffects,
      NavigationEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // Stack trace for actions
      traceLimit: 75, // Maximum stack trace frames
      connectInZone: false, // Connect outside Angular zone to prevent unnecessary change detection
    }),
  ],
})
export class AppBrowserModule {}
